"use client";

/**
 * Global toast store. A lightweight Zustand-style hook implemented with
 * `useSyncExternalStore` so any component can call `toast({...})` without
 * needing a provider higher in the tree. The <Toaster /> component
 * subscribes and renders the queue.
 */
import { useSyncExternalStore } from "react";

type ToastVariant = "default" | "destructive" | "success";

export interface ToastInput {
  title?: string;
  description?: string;
  variant?: ToastVariant;
  /** ms; defaults to 4000. Pass 0 to stay until dismissed. */
  duration?: number;
}

export interface ToastItem extends ToastInput {
  id: string;
  open: boolean;
}

let counter = 0;
const listeners = new Set<() => void>();
let state: ToastItem[] = [];

function emit() {
  for (const l of listeners) l();
}

function subscribe(l: () => void) {
  listeners.add(l);
  return () => listeners.delete(l);
}

function getSnapshot(): ToastItem[] {
  return state;
}

export function useToasts(): ToastItem[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

export function dismissToast(id: string) {
  state = state.map((t) => (t.id === id ? { ...t, open: false } : t));
  emit();
  // Remove from queue shortly after the close animation.
  setTimeout(() => {
    state = state.filter((t) => t.id !== id);
    emit();
  }, 300);
}

export function toast(input: ToastInput): string {
  const id = `t_${++counter}`;
  const item: ToastItem = { ...input, id, open: true };
  state = [...state, item];
  emit();
  const duration = input.duration ?? 4000;
  if (duration > 0) {
    setTimeout(() => dismissToast(id), duration);
  }
  return id;
}
